/**
 * Password Page Drawing Canvas
 * Allows users to draw on the password page with a black, thin pen style
 */

class PasswordDrawing {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.isDrawing = false;
    this.lastX = 0;
    this.lastY = 0;
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupCanvas());
    } else {
      this.setupCanvas();
    }
  }

  setupCanvas() {
    // Find the canvas element
    this.canvas = document.getElementById('password-drawing-canvas');
    
    if (!this.canvas) {
      return;
    }

    this.ctx = this.canvas.getContext('2d');
    
    // Set canvas size to match viewport
    this.resizeCanvas();
    
    // Get drawing color from canvas data attribute
    const drawingColor = this.canvas.getAttribute('data-drawing-color') || '#000000';
    
    // Configure drawing style
    this.ctx.strokeStyle = drawingColor;
    this.ctx.lineWidth = 1.5;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Handle window resize
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  resizeCanvas() {
    if (!this.canvas || !this.ctx) return;
    
    // Save the current canvas content before resizing
    const oldWidth = this.canvas.width;
    const oldHeight = this.canvas.height;
    const hasContent = oldWidth > 0 && oldHeight > 0;
    
    let savedImage = null;
    if (hasContent) {
      // Create a temporary canvas to save the current content
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = oldWidth;
      tempCanvas.height = oldHeight;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(this.canvas, 0, 0);
      savedImage = tempCanvas;
    }
    
    // Get the actual display size
    const rect = this.canvas.getBoundingClientRect();
    const newWidth = rect.width;
    const newHeight = rect.height;
    
    // Set internal size to match display size (this clears the canvas)
    this.canvas.width = newWidth;
    this.canvas.height = newHeight;
    
    // Re-apply drawing style after resize
    const drawingColor = this.canvas.getAttribute('data-drawing-color') || '#000000';
    this.ctx.strokeStyle = drawingColor;
    this.ctx.lineWidth = 1.5;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    
    // Restore the saved drawing if it exists, scaling it to the new size
    if (savedImage && hasContent && newWidth > 0 && newHeight > 0) {
      this.ctx.drawImage(savedImage, 0, 0, oldWidth, oldHeight, 0, 0, newWidth, newHeight);
    }
  }

  setupEventListeners() {
    // Mouse events for desktop
    this.canvas.addEventListener('mousedown', (e) => this.startDrawing(e));
    this.canvas.addEventListener('mousemove', (e) => this.draw(e));
    this.canvas.addEventListener('mouseup', () => this.stopDrawing());
    this.canvas.addEventListener('mouseout', () => this.stopDrawing());
    
    // Touch events for mobile
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.startDrawing(e.touches[0]);
    });
    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      this.draw(e.touches[0]);
    });
    this.canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      this.stopDrawing();
    });
    this.canvas.addEventListener('touchcancel', (e) => {
      e.preventDefault();
      this.stopDrawing();
    });
  }

  getCoordinates(e) {
    const rect = this.canvas.getBoundingClientRect();
    // Handle both mouse and touch events
    const clientX = e.clientX || e.pageX;
    const clientY = e.clientY || e.pageY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  startDrawing(e) {
    // Don't start drawing if clicking on the lock button area
    // The lock button has z-10, so it should naturally be above the canvas,
    // but we check as a safety measure
    const lockButton = document.querySelector('[data-password-lock]');
    if (lockButton) {
      const lockRect = lockButton.getBoundingClientRect();
      const globalX = e.clientX || e.pageX;
      const globalY = e.clientY || e.pageY;
      
      if (globalX >= lockRect.left && globalX <= lockRect.right &&
          globalY >= lockRect.top && globalY <= lockRect.bottom) {
        return;
      }
    }

    this.isDrawing = true;
    const coords = this.getCoordinates(e);
    this.lastX = coords.x;
    this.lastY = coords.y;
  }

  draw(e) {
    if (!this.isDrawing) return;

    const coords = this.getCoordinates(e);
    
    // Clamp coordinates to canvas bounds
    const clampedX = Math.max(0, Math.min(coords.x, this.canvas.width));
    const clampedY = Math.max(0, Math.min(coords.y, this.canvas.height));
    
    // Calculate distance
    const dx = clampedX - this.lastX;
    const dy = clampedY - this.lastY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Skip if mouse jumped too far
    if (distance > 100) {
      this.lastX = clampedX;
      this.lastY = clampedY;
      return;
    }
    
    // Get color (inverted if over image)
    const color = this.getDrawingColor(clampedX, clampedY);
    
    // Thicker strokes like the reference image (3-6px range)
    const pressure = 0.7 + (Math.random() * 0.3); // Simulate pressure variation
    const lineWidth = 3 + (Math.random() * 3) * pressure;
    
    // Opacity variation for expressive quality (0.7-1.0)
    const opacity = 0.7 + (Math.random() * 0.3);
    
    // Smoother drawing - less segments, less jitter
    const segments = Math.min(10, Math.max(1, Math.floor(distance / 8)));
    
    for (let i = 0; i < segments; i++) {
      const t = (i + 1) / segments;
      const x = this.lastX + (dx * t);
      const y = this.lastY + (dy * t);
      
      // Very subtle jitter for organic feel (not scratchy)
      const jitter = 0.3;
      const jitterX = (Math.random() - 0.5) * jitter;
      const jitterY = (Math.random() - 0.5) * jitter;
      
      // Main stroke
      this.ctx.beginPath();
      this.ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`;
      this.ctx.lineWidth = lineWidth;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.moveTo(this.lastX + jitterX, this.lastY + jitterY);
      this.ctx.lineTo(x + jitterX, y + jitterY);
      this.ctx.stroke();
      
      this.lastX = x;
      this.lastY = y;
    }
    
    // Update to final position
    this.lastX = clampedX;
    this.lastY = clampedY;
  }

  getDrawingColor(x, y) {
    // Default to black
    const baseColor = this.canvas.getAttribute('data-drawing-color') || '#000000';
    const baseRgb = this.hexToRgb(baseColor);
    
    // Get viewport coordinates
    const rect = this.canvas.getBoundingClientRect();
    const viewportX = rect.left + x;
    const viewportY = rect.top + y;
    
    // Find all images on the page and check which one contains this point
    const allImages = document.querySelectorAll('img');
    let targetImg = null;
    let imgRect = null;
    
    for (const img of allImages) {
      // Skip if image not loaded or not visible
      if (!img.complete || img.naturalWidth === 0) continue;
      
      const rect = img.getBoundingClientRect();
      const style = window.getComputedStyle(img);
      
      // Skip if hidden
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
        continue;
      }
      
      // Check if point is within this image
      if (viewportX >= rect.left && viewportX <= rect.right &&
          viewportY >= rect.top && viewportY <= rect.bottom) {
        targetImg = img;
        imgRect = rect;
        break;
      }
    }
    
    // If no image found, return base color
    if (!targetImg) return baseRgb;
    
    // Calculate position within image
    const imgX = viewportX - imgRect.left;
    const imgY = viewportY - imgRect.top;
    
    // Sample the image at this point
    try {
      // Create temp canvas for sampling
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 1;
      tempCanvas.height = 1;
      const tempCtx = tempCanvas.getContext('2d');
      
      // Calculate scale (image might be scaled via CSS)
      const scaleX = targetImg.naturalWidth / imgRect.width;
      const scaleY = targetImg.naturalHeight / imgRect.height;
      
      // Try to draw and sample - this will fail with CORS if image doesn't allow it
      tempCtx.drawImage(
        targetImg,
        imgX * scaleX, imgY * scaleY, 1, 1, // Source: 1x1 pixel
        0, 0, 1, 1 // Destination
      );
      
      const imageData = tempCtx.getImageData(0, 0, 1, 1);
      const data = imageData.data;
      
      // Get RGB values
      const r = data[0];
      const g = data[1];
      const b = data[2];
      
      // Calculate luminance
      const luminance = this.getLuminance(r, g, b);
      
      // Invert: if image is dark, use white; if light, use black
      if (luminance < 0.5) {
        return { r: 255, g: 255, b: 255 }; // White for dark areas
      } else {
        return { r: 0, g: 0, b: 0 }; // Black for light areas
      }
    } catch (e) {
      // CORS error or other issue - can't read pixel data
      // This is the most common reason it doesn't work
      // The image needs crossOrigin="anonymous" attribute and proper CORS headers
      return baseRgb;
    }
  }

  parseRgb(rgbString) {
    // Parse rgb(r, g, b) or rgba(r, g, b, a) format
    const match = rgbString.match(/\d+/g);
    if (match && match.length >= 3) {
      return {
        r: parseInt(match[0]),
        g: parseInt(match[1]),
        b: parseInt(match[2])
      };
    }
    return { r: 255, g: 255, b: 255 };
  }

  getLuminance(r, g, b) {
    // Calculate relative luminance using WCAG formula
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  hexToRgb(hex) {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Handle 3-digit hex
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return { r, g, b };
  }

  stopDrawing() {
    this.isDrawing = false;
  }
}

// Initialize when module loads
new PasswordDrawing();

